import { Copy, Mail } from "lucide-react";
import type { ReactNode } from "react";
import { toast } from "sonner";
import { Hint } from "@/components/ui/tooltip";

type CopyableAddressProps = {
  address: string | null;
  label: string;
  /** Borderless inline form for dense contexts like table cells. */
  compact?: boolean;
  /** Styled tooltip shown on hover/focus instead of the native title. */
  tooltip?: ReactNode;
};

async function copy(address: string) {
  try {
    await navigator.clipboard.writeText(address);
    toast.success("Address copied");
  } catch (error) {
    console.error("Failed to copy address", error);
    toast.error("Failed to copy address");
  }
}

export function CopyableAddress({
  address,
  label,
  compact = false,
  tooltip,
}: CopyableAddressProps) {
  if (!address) {
    return (
      <span className="text-sm text-muted-foreground">Address pending</span>
    );
  }

  const trigger = compact ? (
    <button
      type="button"
      onClick={() => void copy(address)}
      title={tooltip ? undefined : `Copy ${label}`}
      aria-label={`Copy ${label}`}
      className="font-meta inline-flex max-w-full items-center gap-1.5 rounded-sm text-left text-xs text-muted-foreground transition hover:text-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/45 focus-visible:outline-none"
    >
      <span className="break-all">{address}</span>
      <Copy className="h-3 w-3 shrink-0" aria-hidden="true" />
    </button>
  ) : (
    <button
      type="button"
      onClick={() => void copy(address)}
      title={tooltip ? undefined : `Copy ${label}`}
      aria-label={`Copy ${label}`}
      className="group font-meta inline-flex w-full items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-1.5 text-left text-xs text-foreground transition hover:bg-muted focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/45 focus-visible:outline-none"
    >
      <Mail className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="address-marquee min-w-0 flex-1">
        <span>{address}</span>
      </span>
      <Copy className="h-4 w-4 shrink-0" aria-hidden="true" />
    </button>
  );

  return tooltip ? <Hint content={tooltip}>{trigger}</Hint> : trigger;
}
